/**
 * Web Audio sound effects for the call module.
 *
 * The user has already interacted with the page by the time these fire
 * (clicked Join, then clicked a control button), so the AudioContext
 * autoplay restriction has been lifted. Silently degrades if the API is
 * unavailable.
 */

interface ToneSpec {
  /** Start offset in seconds, relative to now. */
  offset: number;
  /** Pitch in Hz. */
  frequency: number;
  /** Sustain in seconds. */
  duration: number;
  /** Peak gain (0–1). Stay below ~0.15 to keep the level pleasant. */
  peak: number;
}

function playTones(tones: ToneSpec[]): void {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;

    const ctx = new Ctx();
    const now = ctx.currentTime;
    let maxStop = now;

    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain).connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = t.frequency;
      const start = now + t.offset;
      const stop = start + t.duration;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(t.peak, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, stop);
      osc.start(start);
      osc.stop(stop + 0.02);
      if (stop > maxStop) maxStop = stop;
    }

    // Close the context a little after the last tone finishes so we don't
    // leak audio nodes per call.
    const closeAfterMs = Math.max(300, Math.ceil((maxStop - now) * 1000) + 200);
    window.setTimeout(() => {
      ctx.close().catch(() => {});
    }, closeAfterMs);
  } catch {
    // Best-effort — sound is non-critical.
  }
}

/** Two-tone notification ding (880 → 1320 Hz). Used by chat receive. */
export function playChatPing(): void {
  playTones([
    { offset: 0, frequency: 880, duration: 0.18, peak: 0.12 },
    { offset: 0.11, frequency: 1320, duration: 0.22, peak: 0.1 },
  ]);
}

/** Light playful three-note pop — used when sending an emoji reaction. */
export function playReaction(): void {
  playTones([
    { offset: 0, frequency: 660, duration: 0.07, peak: 0.09 },
    { offset: 0.05, frequency: 990, duration: 0.07, peak: 0.08 },
    { offset: 0.1, frequency: 1320, duration: 0.12, peak: 0.07 },
  ]);
}

/** Short ascending blip — mic / camera switched ON. */
export function playToggleOn(): void {
  playTones([
    { offset: 0, frequency: 440, duration: 0.06, peak: 0.08 },
    { offset: 0.045, frequency: 880, duration: 0.1, peak: 0.08 },
  ]);
}

/** Short descending blip — mic / camera switched OFF. */
export function playToggleOff(): void {
  playTones([
    { offset: 0, frequency: 880, duration: 0.06, peak: 0.08 },
    { offset: 0.045, frequency: 440, duration: 0.1, peak: 0.08 },
  ]);
}

/** Convenience — picks `playToggleOn` or `playToggleOff` based on the new state. */
export function playToggle(on: boolean): void {
  if (on) playToggleOn();
  else playToggleOff();
}

/**
 * Two-tone descending "hang-up" beep — D5 → A4. Used by user-initiated
 * end-call from MeetingControls and the FloatingCallWidget pill. NOT
 * fired on LiveKit's onDisconnected (network drops shouldn't beep).
 */
export function playCallEnd(): void {
  playTones([
    { offset: 0, frequency: 587, duration: 0.13, peak: 0.1 },
    { offset: 0.12, frequency: 440, duration: 0.22, peak: 0.1 },
  ]);
}
